<template>
  <div class="comment-list">
    <div class="comment-list-form">
      <div class="comment-list-input">
        <InputText
            placeholder="Digite a nota e enter"
            field="note"
            :formName="formName"
            :borderRadius="50"
            :maxLength="300"
            :enter="addNote"
            v-model="newNote"
        />
      </div>
      <div class="comment-list-action">
        <Button
            _key="btnAddComment"
            type="primary"
            classIcon="fa-sharp fa-solid fa-plus"
            size="small"
            :loading="isLoading('btnAddComment')"
            :disabled="!canAddNote"
            :clicked="addNote"
        />
      </div>
    </div>

    <Loading v-if="loading" type="line"/>

    <ScrollBar :minHeight="420" :maxHeight="420">
      <div>
        <div v-if="comments.length === 0" class="comment-list-empty">
          <i class="fa-regular fa-comments"></i>
          <span>Nenhuma nota adicionada!</span>
        </div>
        <div
            v-for="comment in comments"
            :key="comment.id"
            class="comment-list-item"
        >
          <img :src="comment.avatar" class="comment-list-avatar" alt="Usuário"/>
          <div class="comment-list-content">
            <div class="comment-list-meta">
              {{ comment.userName }} · {{ comment.registeredDateStr }}
            </div>

            <div v-if="editingComment.id !== comment.id">
              <div class="comment-list-bubble" @click="startEdit(comment)">
                {{ comment.note }}
              </div>
            </div>

            <div v-else class="comment-list-edit-row">
              <input
                  class="comment-list-edit-input"
                  type="text"
                  v-model="editingComment.note"
                  @keyup.enter.prevent="saveEdit"
              />
              <div class="comment-list-edit-actions">
                <div class="comment-list-icon-save" @click="saveEdit">
                  <i class="fa-solid fa-floppy-disk"></i>
                </div>
                <div class="comment-list-icon-cancel" @click="cancelEdit">
                  <i class="fa-solid fa-ban"></i>
                </div>
                <div class="comment-list-icon-danger" @click="removeComment(comment)">
                  <i class="fa-solid fa-trash"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollBar>
  </div>
</template>

<script>
import ScrollBar from "@/ui/layout/ScrollBar.vue";

import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";

import Loading from "@/ui/shared/Loading.vue";

import CommentService from "../../services/comment.service";

import {mapGetters, mapMutations, mapState} from "vuex";

export default {
  name: "CommentList",
  components: {Button, InputText, ScrollBar, Loading},
  props: {
    cardId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      formName: "commentListForm",
      newNote: "",
      comments: [],
      editingComment: {},
      loading: true,
    };
  },
  mounted() {
    this.loadComments();
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapGetters("generic", ["isLoading"]),
    canAddNote() {
      return !!this.newNote.trim();
    },
  },
  methods: {
    ...mapMutations("generic", ["addLoading", "removeLoading"]),
    async loadComments() {
      this.loading = true;
      const response = await CommentService.getAll(this.cardId);
      this.comments = response.data.content.data;
      this.loading = false;
      this.$emit("count-change", this.comments.length);
    },
    async addNote() {
      if (!this.canAddNote) return;
      this.addLoading("btnAddComment");
      try {
        await CommentService.create(this.cardId, this.newNote.trim(), this.userLogged.user.id);
        this.newNote = "";
        await this.loadComments();
      } finally {
        this.removeLoading(["btnAddComment"]);
      }
    },
    startEdit(comment) {
      this.editingComment = {...comment};
    },
    cancelEdit() {
      this.editingComment = {};
    },
    async saveEdit() {
      await CommentService.update(this.editingComment.id, this.editingComment.note);
      this.editingComment = {};
      await this.loadComments();
    },
    async removeComment(comment) {
      await CommentService.remove(comment.id);
      await this.loadComments();
    },
  },
};
</script>

<style scoped>
.comment-list {
  margin-top: -25px;
}

.comment-list-form {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-list-input {
  flex: 1;
  min-width: 0;
}

.comment-list-action {
  flex-shrink: 0;
  margin-top: 38px;
  margin-bottom: 20px;
}

.comment-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--text-muted);
  font-size: 14px;
}

.comment-list-empty i {
  font-size: 28px;
}

.comment-list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}

.comment-list-avatar {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 2px;
}

.comment-list-content {
  flex: 1;
  min-width: 0;
}

.comment-list-meta {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.comment-list-bubble {
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s;
  word-break: break-word;
}

.comment-list-bubble:hover {
  background: var(--bg-hover);
}

.comment-list-edit-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.comment-list-edit-input {
  flex: 1;
  padding: 8px 14px;
  border: 1px solid var(--border-input);
  border-radius: 999px;
  background: var(--input-bg);
  color: var(--input-color);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
}

.comment-list-edit-input:focus {
  border-color: var(--color-brand-light);
}

.comment-list-edit-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-list-icon-save {
  font-size: 18px;
  color: var(--color-success);
  cursor: pointer;
  transition: opacity 0.15s;
}

.comment-list-icon-save:hover {
  opacity: 0.7;
}

.comment-list-icon-cancel {
  font-size: 18px;
  color: var(--color-danger);
  cursor: pointer;
  transition: opacity 0.15s;
}

.comment-list-icon-cancel:hover {
  opacity: 0.7;
}

.comment-list-icon-danger {
  font-size: 18px;
  color: var(--color-danger);
  cursor: pointer;
  transition: opacity 0.15s;
}

.comment-list-icon-danger:hover {
  opacity: 0.7;
}
</style>
